import { useState, useEffect } from 'react';
import { Workbox } from 'workbox-window';
import { analytics } from '../services/analytics';

export const usePWA = () => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [wb, setWb] = useState<Workbox | null>(null);

  useEffect(() => {
    // Reload an already controlled page once when a new release worker takes over.
    const wasControlled = !!navigator.serviceWorker?.controller;
    const onController = () => { if (wasControlled) window.location.reload(); };
    navigator.serviceWorker?.addEventListener('controllerchange', onController);
    // 1. Handle Install Prompt
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setInstallPrompt(e);
      analytics.trackEvent('pwa_install_available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 2. Handle Service Worker Updates
    if ('serviceWorker' in navigator) {
      // Using relative path to sw.js to prevent same-origin scope errors in subfolder-hosted environments
      const workbox = new Workbox('sw.js');

      workbox.addEventListener('waiting', () => {
        setIsUpdateAvailable(true);
        analytics.trackEvent('pwa_update_available');
      });

      workbox.register().catch(err => {
        console.warn('SW registration failed:', err);
      });
      
      setWb(workbox);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      navigator.serviceWorker?.removeEventListener('controllerchange', onController);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    analytics.trackEvent('pwa_install_choice', { outcome });
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const applyUpdate = () => {
    if (wb) {
      wb.addEventListener('controlling', () => {
        window.location.reload();
      });
      wb.messageSkipWaiting();
    }
  };

  return {
    canInstall: !!installPrompt,
    promptInstall,
    isUpdateAvailable,
    applyUpdate
  };
};